import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type LanguageType = 'BR-pt' | 'US-en';

interface LanguageContextType {
  language: LanguageType;
  setLanguage: (lang: LanguageType) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<LanguageType, Record<string, string>> = {
  'BR-pt': {
    // General / Common
    'app.title': 'Draft do Bill of Lading',
    'common.save': 'Salvar',
    'common.cancel': 'Cancelar',
    'common.loading': 'Carregando...',
    'common.error': 'Erro',
    'common.success': 'Sucesso',
    'common.warning': 'Aviso',
    'common.info': 'Informação',
    'common.remove': 'Remover',
    'common.add': 'Adicionar',
    'common.edit': 'Editar',
    'common.close': 'Fechar',
    'common.submit': 'Enviar',
    'common.back': 'Voltar',
    
    // Login Page
    'login.title': 'Login - Despachante Aduaneiro',
    'login.locked': 'Conta temporariamente bloqueada',
    'login.locked_desc': 'Muitas tentativas incorretas. Por favor, aguarde {time} antes de tentar novamente.',
    'login.attempts_left': '{count} tentativa(s) restante(s) antes do bloqueio temporário',
    'login.email_label': 'Endereço de E-mail',
    'login.email_placeholder': 'seu.email@dominio-despachante.com',
    'login.email_helper': 'Use o seu e-mail de despachante aduaneiro registrado',
    'login.email_required': 'O e-mail é obrigatório',
    'login.email_invalid': 'Por favor, insira um e-mail válido',
    'login.password_label': 'Senha',
    'login.password_placeholder': 'Digite sua senha',
    'login.password_required': 'A senha é obrigatória',
    'login.password_min_length': 'A senha deve ter pelo menos 8 caracteres',
    'login.forgot_password': 'Esqueceu a senha?',
    'login.sign_in': 'Entrar',
    'login.first_time': 'Primeira vez aqui?',
    'login.create_account': 'Criar Conta',
    'login.note': 'Apenas despachantes aduaneiros verificados com e-mails de domínio registrado podem acessar este sistema.',
    'login.success_title': 'Login bem-sucedido',
    'login.success_desc': 'Bem-vindo de volta!',
    'login.failed_title': 'Falha no login',
    'login.failed_desc': 'E-mail ou senha inválidos',
    
    // Register Page
    'register.title': 'Criar Conta de Despachante Aduaneiro',
    'register.subtitle': 'Registre-se para acessar o Draft do Bill of Lading',
    'register.domain_verified': 'Domínio Verificado',
    'register.email_label': 'Endereço de E-mail',
    'register.email_placeholder': 'seu.email@dominio-despachante.com',
    'register.email_helper': 'Deve corresponder a um domínio de despachante registrado',
    'register.email_required': 'O e-mail é obrigatório',
    'register.email_invalid': 'Por favor, insira um e-mail válido',
    'register.email_domain_invalid': 'O domínio do e-mail deve corresponder a um despachante aduaneiro registrado',
    'register.email_validation_error': 'Não foi possível validar o domínio. Tente novamente.',
    'register.password_label': 'Senha',
    'register.password_placeholder': 'Crie uma senha forte',
    'register.password_required': 'A senha é obrigatória',
    'register.password_strength': 'Força da Senha:',
    'register.requirements': 'Requisitos:',
    'register.req.min_length': 'Pelo menos 8 caracteres',
    'register.req.uppercase': 'Uma letra maiúscula',
    'register.req.lowercase': 'Uma letra minúscula',
    'register.req.number': 'Um número',
    'register.req.special': 'Um caractere especial',
    'register.strength.weak': 'Fraca',
    'register.strength.fair': 'Razoável',
    'register.strength.good': 'Boa',
    'register.strength.strong': 'Forte',
    'register.confirm_password_label': 'Confirmar Senha',
    'register.confirm_password_placeholder': 'Digite novamente sua senha',
    'register.confirm_password_required': 'Por favor, confirme sua senha',
    'register.passwords_match': 'As senhas coincidem',
    'register.passwords_mismatch': 'As senhas não coincidem',
    'register.password_strength_invalid': 'A senha não atende aos requisitos de segurança',
    'register.create_btn': 'Criar Conta',
    'register.creating': 'Criando conta...',
    'register.already_have_account': 'Já tem uma conta?',
    'register.sign_in_btn': 'Entrar',
    'register.note': 'O domínio do seu e-mail será verificado em nosso banco de dados de despachantes aduaneiros registrados. Apenas despachantes cadastrados podem se registrar.',
    'register.success_title': 'Registro bem-sucedido',
    'register.success_desc': 'Bem-vindo! Sua conta foi criada.',
    'register.failed_title': 'Falha no registro',
    'register.failed_desc': 'Não foi possível criar a conta',
    
    // Forgot Password Page
    'forgot.title': 'Redefinir Senha',
    'forgot.subtitle_check': 'Verifique seu e-mail para instruções de redefinição',
    'forgot.subtitle_enter': 'Insira seu e-mail para receber as instruções de redefinição de senha',
    'forgot.alert_info': 'Enviaremos as instruções de redefinição para o seu e-mail registrado.',
    'forgot.email_label': 'Endereço de E-mail',
    'forgot.email_placeholder': 'seu.email@dominio-despachante.com',
    'forgot.email_helper': 'Use o seu e-mail de despachante registrado',
    'forgot.email_required': 'O e-mail é obrigatório',
    'forgot.email_invalid': 'Por favor, insira um e-mail válido',
    'forgot.send_btn': 'Enviar Instruções',
    'forgot.sending': 'Enviando...',
    'forgot.back_login': 'Voltar para o Login',
    'forgot.success_title': 'E-mail Enviado!',
    'forgot.success_desc': 'Se existir uma conta para {email}, você receberá instruções de redefinição em breve.',
    'forgot.next_steps': 'Próximos Passos:',
    'forgot.step_1': '1. Verifique sua caixa de entrada (e caixa de spam)',
    'forgot.step_2': '2. Clique no link de redefinição contido no e-mail',
    'forgot.step_3': '3. Crie uma nova senha',
    'forgot.step_4': '4. Entre com a sua nova senha',
    'forgot.send_again': 'Enviar Novamente',
    'forgot.security_note': 'Nota de Segurança: Para sua proteção, não confirmamos se o e-mail está cadastrado em nosso sistema.',
    'forgot.toast_success': 'E-mail de redefinição enviado',
    'forgot.toast_failed': 'Falha na solicitação',
    
    // Dashboard Page
    'dashboard.title': 'Painel de Embarques',
    'dashboard.loading': 'Carregando painel...',
    'dashboard.no_data': 'Nenhum dado do painel disponível',
    'dashboard.total_shipments': 'Total de Embarques',
    'dashboard.total_shipments_help': 'Todos os embarques atribuídos',
    'dashboard.draft': 'Rascunho',
    'dashboard.draft_help': 'Não iniciado',
    'dashboard.in_progress': 'Em Andamento',
    'dashboard.in_progress_help': 'Sendo processado',
    'dashboard.submitted': 'Enviado',
    'dashboard.submitted_help': 'Sob revisão',
    'dashboard.completed': 'Concluído',
    'dashboard.completed_help': 'Finalizado',
    'dashboard.search_placeholder': 'Buscar por número de embarque, consignatário ou navio...',
    'dashboard.status_label': 'Status:',
    'dashboard.sort_label': 'Ordenar:',
    'dashboard.sort_created': 'Data de Criação',
    'dashboard.sort_etd': 'Data de Saída (ETD)',
    'dashboard.sort_ref': 'Embarque #',
    'dashboard.sort_status': 'Status',
    'dashboard.per_page': 'Por página:',
    'dashboard.active_filters': 'Filtros ativos:',
    'dashboard.clear_all': 'Limpar tudo',
    'dashboard.refresh_success': 'Atualizado',
    'dashboard.refresh_desc': 'Dados do painel atualizados',
    'dashboard.logout_success': 'Deslogado',
    'dashboard.logout_desc': 'Você foi deslogado com sucesso',
    
    // ShipmentTable / Pagination / Due Modal
    'table.loading': 'Carregando embarques...',
    'table.failed': 'Falha ao carregar embarques',
    'table.no_shipments_title': 'Nenhum Embarque Encontrado',
    'table.no_shipments_desc': 'Tente ajustar seus filtros ou termos de busca.',
    'table.no_shipments_assigned': 'Nenhum embarque atribuído ao seu despachante no momento. Verifique novamente mais tarde ou contate o suporte.',
    'table.col_shipment': 'Embarque',
    'table.col_shipper': 'Exportador',
    'table.col_order': 'Pedido',
    'table.col_booking': 'Reserva (Booking)',
    'table.col_vessel': 'Navio',
    'table.col_draft': 'Prazo Rascunho',
    'table.col_due_status': 'Status DUE',
    'table.col_actions': 'Ações',
    'table.showing': 'Exibindo {start} a {end} de {total} embarques',
    'table.due_modal_title': 'Inserir Número da DUE',
    'table.due_label': 'Número da DUE',
    'table.due_placeholder': 'Digite o número da DUE',
    'table.due_shipment_details': 'Detalhes do Embarque',
    'table.due_shipper': 'Exportador',
    'table.due_required': 'Número da DUE é obrigatório',
    'table.due_required_desc': 'Por favor, insira o número da DUE para continuar.',
    'table.due_saved_title': 'Número da DUE Salvo',
    'table.due_saved_desc': 'Prosseguindo para o formulário de Draft do BL...',
    'table.pdf_generating_title': 'Gerando PDF',
    'table.pdf_generating_desc': 'Aguarde enquanto geramos o seu Bill of Lading...',
    'table.pdf_success_title': 'Sucesso',
    'table.pdf_success_desc': 'PDF do Bill of Lading gerado com sucesso',
    'table.pdf_failed_title': 'Erro',
    'table.pdf_failed_desc': 'Falha ao gerar o PDF. Tente novamente.',
    'table.due_save_failed': 'Falha ao salvar número da DUE',
    
    // ShipmentDetailsModal
    'details.title': 'Detalhes do Embarque',
    'details.loading': 'Carregando detalhes do embarque...',
    'details.no_data': 'Nenhum dado de embarque disponível',
    'details.status': 'Status',
    'details.booking_ref': 'Referência da Reserva',
    'details.parties': 'Partes',
    'details.shipper': 'Exportador (Shipper)',
    'details.consignee': 'Consignatário (Consignee)',
    'details.transport_route': 'Transporte & Rota',
    'details.vessel_voyage': 'Navio / Viagem',
    'details.pol': 'Porto de Carregamento (POL)',
    'details.pod': 'Porto de Descarga (POD)',
    'details.place_delivery': 'Local de Entrega',
    'details.dates': 'Datas Importantes',
    'details.etd': 'ETD (Saída Estimada)',
    'details.deadline_draft': 'Prazo para Rascunho',
    'details.draft_submitted': 'Rascunho Enviado',
    'details.cargo_details': 'Detalhes da Carga',
    'details.description': 'Descrição',
    'details.gross_weight': 'Peso Bruto',
    'details.measurement': 'Metragem Cúbica (CBM)',
    'details.containers': 'Contêineres ({count})',
    'details.col_container': 'Contêiner #',
    'details.col_type': 'Tipo',
    'details.col_seal': 'Lacre',
    'details.col_tare': 'Tara',
    'details.marks_numbers': 'Marcas e Números',
    'details.close_btn': 'Fechar',
    'details.edit_draft': 'Editar Rascunho',
    
    // UserProfileDrawer
    'profile.title': 'Configurações do Perfil',
    'profile.subtitle': 'Gerencie suas informações de conta',
    'profile.email': 'Endereço de E-mail',
    'profile.email_helper': 'O e-mail não pode ser alterado',
    'profile.display_name': 'Nome de Exibição',
    'profile.display_name_placeholder': 'Digite seu nome de exibição',
    'profile.full_name': 'Nome Completo',
    'profile.full_name_placeholder': 'Digite seu nome completo',
    'profile.phone': 'Telefone',
    'profile.phone_placeholder': 'Digite seu telefone',
    'profile.broker': 'Despachante (Broker)',
    'profile.broker_helper': 'Entre em contato com o administrador para alterar o despachante',
    'profile.logout_btn': 'Sair',
    'profile.cancel_btn': 'Cancelar',
    'profile.save_btn': 'Salvar Alterações',
    'profile.success_title': 'Perfil Atualizado',
    'profile.success_desc': 'Seu perfil foi atualizado com sucesso.',
    'profile.failed_title': 'Falha na Atualização',
    'profile.failed_desc': 'Falha ao atualizar o perfil. Tente novamente.',
    'profile.coming_soon': 'Em Breve',
    'profile.coming_soon_desc': 'A função de upload de avatar estará disponível em breve.',
    'profile.avatar_helper': 'Clique no ícone da câmera para alterar o avatar',
    'profile.avatar_aria': 'Alterar avatar',
    'profile.language_label': 'Idioma / Language',
    'profile.lang_pt': '🇧🇷 Português (BR-pt)',
    'profile.lang_en': '🇺🇸 English (US-en)',
    
    // DraftBillOfLadingTab
    'draft.loading': 'Carregando embarque...',
    'draft.no_shipment_title': 'Selecione um embarque para criar o Bill of Lading',
    'draft.no_shipment_desc': 'Selecione um embarque no painel para continuar',
    'draft.back_dashboard': 'Voltar para o Painel',
    'draft.badge_saved': 'Rascunho Salvo',
    'draft.form_subtitle': 'DRAFT OF BILL OF LADING FCL / FCL',
    'draft.submit_btn': 'Enviar RASCUNHO',
    'draft.pdf_btn': 'RASCUNHO pdf',
    'draft.shipper': 'Shipper:',
    'draft.shipper_placeholder': 'Nome do exportador',
    'draft.shipper_addr_placeholder': 'Endereço do exportador',
    'draft.consignee': 'Consignee:',
    'draft.consignee_placeholder': 'Nome do consignatário',
    'draft.consignee_addr_placeholder': 'Endereço do consignatário',
    'draft.notify_party': 'Notify Party:',
    'draft.notify_copy': 'Copiar do Consignee',
    'draft.notify_placeholder': 'Nome da parte notificada',
    'draft.notify_addr_placeholder': 'Endereço da parte notificada',
    'draft.delivery_apply': 'For delivery of the goods apply to:',
    'draft.delivery_placeholder': 'Instruções de entrega',
    'draft.pre_carriage': 'Pre-carriage by:',
    'draft.pre_carriage_placeholder': 'Método de pré-transporte',
    'draft.vessel_voyage': 'Vessel | Voyage:',
    'draft.vessel_placeholder': 'Navio',
    'draft.voyage_placeholder': 'Viagem',
    'draft.port_discharge': 'Port of discharge:',
    'draft.port_discharge_placeholder': 'Porto de descarga',
    'draft.place_receipt': 'Place of receipt by pre-carrier:',
    'draft.place_receipt_placeholder': 'Local de recebimento',
    'draft.port_loading': 'Port of loading:',
    'draft.port_loading_placeholder': 'Porto de carregamento',
    'draft.place_delivery': 'Place of delivery by on-carrier:',
    'draft.place_delivery_placeholder': 'Local de entrega',
    'draft.marks_numbers': 'Marks and numbers:',
    'draft.marks_numbers_placeholder': 'Marcas e números do embarque',
    'draft.ncm_hs': 'NCM / HS Code:',
    'draft.ncm_hs_placeholder': 'Código NCM / HS',
    'draft.gross_weight': 'Gross Weight (KGS):',
    'draft.gross_weight_placeholder': 'Peso bruto total',
    'draft.measurement': 'Measurement (CBM):',
    'draft.measurement_placeholder': 'Metragem cúbica total',
    'draft.freight_charges': 'FREIGHT AND CHARGES:',
    'draft.freight_placeholder': 'Detalhes do frete',
    'draft.prepaid': 'PREPAID',
    'draft.collect': 'COLLECT',
    'draft.freight_payable': 'Freight payable at:',
    'draft.freight_payable_placeholder': 'Frete a pagar em',
    'draft.original_bl': 'Number of Original B(s)/L:',
    'draft.place_date_issue': 'Place and date of issue:',
    'draft.place_date_issue_placeholder': 'Local e data de emissão',
    'draft.signature': 'Signature:',
    'draft.signature_placeholder': 'Assinatura',
    'draft.cargo_description': 'Cargo description / Particulars furnished by shipper:',
    'draft.cargo_desc_placeholder': 'Descrição detalhada da mercadoria',
    'draft.container_details': 'Container details:',
    'draft.container_num': 'Container Number',
    'draft.container_type': 'Container Type',
    'draft.container_tare': 'Tare Weight',
    'draft.container_gross': 'Gross Weight',
    'draft.container_net': 'Net Weight',
    'draft.container_cbm': 'CBM',
    'draft.container_qty': 'Qty',
    'draft.container_seal': 'Seal Number',
    'draft.container_vgm': 'VGM',
    'draft.add_container': 'Adicionar Contêiner',
    'draft.remove_btn': 'Remover',
    'draft.toast_autofill_title': 'Auto-preenchido',
    'draft.toast_autofill_desc': 'Formulário do BL preenchido com dados do embarque',
    'draft.toast_submitted_title': 'Enviado',
    'draft.toast_submitted_desc': 'Rascunho enviado com sucesso. Agora você pode gerar o PDF.',
    'draft.toast_saved_title': 'Salvo',
    'draft.toast_saved_desc': 'Rascunho e detalhes do contêiner salvos com sucesso',
    'draft.toast_error_save': 'Falha ao salvar rascunho',
    'draft.toast_pdf_title': 'Geração de PDF',
    'draft.toast_pdf_desc': 'Recurso de geração de PDF disponível após envio'
  },
  'US-en': {
    // General / Common
    'app.title': 'Draft Bill of Lading',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.warning': 'Warning',
    'common.info': 'Info',
    'common.remove': 'Remove',
    'common.add': 'Add',
    'common.edit': 'Edit',
    'common.close': 'Close',
    'common.submit': 'Submit',
    'common.back': 'Back',
    
    // Login Page
    'login.title': 'Login - Customs Broker',
    'login.locked': 'Account temporarily locked',
    'login.locked_desc': 'Too many failed attempts. Please wait {time} before trying again.',
    'login.attempts_left': '{count} attempt(s) remaining before temporary lockout',
    'login.email_label': 'Email Address',
    'login.email_placeholder': 'your.email@broker-domain.com',
    'login.email_helper': 'Use your registered customs broker email',
    'login.email_required': 'Email is required',
    'login.email_invalid': 'Please enter a valid email address',
    'login.password_label': 'Password',
    'login.password_placeholder': 'Enter your password',
    'login.password_required': 'Password is required',
    'login.password_min_length': 'Password must be at least 8 characters',
    'login.forgot_password': 'Forgot Password?',
    'login.sign_in': 'Sign In',
    'login.first_time': 'First time here?',
    'login.create_account': 'Create Account',
    'login.note': 'Only verified customs brokers with registered domain emails can access this system.',
    'login.success_title': 'Login successful',
    'login.success_desc': 'Welcome back!',
    'login.failed_title': 'Login failed',
    'login.failed_desc': 'Invalid email or password',
    
    // Register Page
    'register.title': 'Create Customs Broker Account',
    'register.subtitle': 'Register to access Draft Bill of Lading',
    'register.domain_verified': 'Domain Verified',
    'register.email_label': 'Email Address',
    'register.email_placeholder': 'your.email@broker-domain.com',
    'register.email_helper': 'Must match a registered customs broker domain',
    'register.email_required': 'Email is required',
    'register.email_invalid': 'Please enter a valid email address',
    'register.email_domain_invalid': 'Email domain must match a registered Customs Broker',
    'register.email_validation_error': 'Unable to validate domain. Please try again.',
    'register.password_label': 'Password',
    'register.password_placeholder': 'Create a strong password',
    'register.password_required': 'Password is required',
    'register.password_strength': 'Password Strength:',
    'register.requirements': 'Requirements:',
    'register.req.min_length': 'At least 8 characters',
    'register.req.uppercase': 'One uppercase letter',
    'register.req.lowercase': 'One lowercase letter',
    'register.req.number': 'One number',
    'register.req.special': 'One special character',
    'register.strength.weak': 'Weak',
    'register.strength.fair': 'Fair',
    'register.strength.good': 'Good',
    'register.strength.strong': 'Strong',
    'register.confirm_password_label': 'Confirm Password',
    'register.confirm_password_placeholder': 'Re-enter your password',
    'register.confirm_password_required': 'Please confirm your password',
    'register.passwords_match': 'Passwords match',
    'register.passwords_mismatch': 'Passwords do not match',
    'register.password_strength_invalid': 'Password does not meet strength requirements',
    'register.create_btn': 'Create Account',
    'register.creating': 'Creating account...',
    'register.already_have_account': 'Already have an account?',
    'register.sign_in_btn': 'Sign In',
    'register.note': 'Your email domain will be verified against our database of registered customs brokers. Only primary brokers with matching domains can register.',
    'register.success_title': 'Registration successful',
    'register.success_desc': 'Welcome! Your account has been created.',
    'register.failed_title': 'Registration failed',
    'register.failed_desc': 'Unable to create account',
    
    // Forgot Password Page
    'forgot.title': 'Reset Password',
    'forgot.subtitle_check': 'Check your email for reset instructions',
    'forgot.subtitle_enter': 'Enter your email to receive password reset instructions',
    'forgot.alert_info': 'We\'ll send password reset instructions to your registered email address.',
    'forgot.email_label': 'Email Address',
    'forgot.email_placeholder': 'your.email@broker-domain.com',
    'forgot.email_helper': 'Use your registered customs broker email',
    'forgot.email_required': 'Email is required',
    'forgot.email_invalid': 'Please enter a valid email address',
    'forgot.send_btn': 'Send Reset Instructions',
    'forgot.sending': 'Sending...',
    'forgot.back_login': 'Back to Login',
    'forgot.success_title': 'Email Sent!',
    'forgot.success_desc': 'If an account exists for {email}, you will receive password reset instructions shortly.',
    'forgot.next_steps': 'Next Steps:',
    'forgot.step_1': '1. Check your email inbox (and spam folder)',
    'forgot.step_2': '2. Click the reset link in the email',
    'forgot.step_3': '3. Create a new password',
    'forgot.step_4': '4. Sign in with your new password',
    'forgot.send_again': 'Send Again',
    'forgot.security_note': 'Security Note: For your protection, we won\'t confirm whether this email exists in our system.',
    'forgot.toast_success': 'Reset email sent',
    'forgot.toast_failed': 'Request failed',
    
    // Dashboard Page
    'dashboard.title': 'Shipment Dashboard',
    'dashboard.loading': 'Loading dashboard...',
    'dashboard.no_data': 'No dashboard data available',
    'dashboard.total_shipments': 'Total Shipments',
    'dashboard.total_shipments_help': 'All assigned shipments',
    'dashboard.draft': 'Draft',
    'dashboard.draft_help': 'Not started',
    'dashboard.in_progress': 'In Progress',
    'dashboard.in_progress_help': 'Being processed',
    'dashboard.submitted': 'Submitted',
    'dashboard.submitted_help': 'Under review',
    'dashboard.completed': 'Completed',
    'dashboard.completed_help': 'Finalized',
    'dashboard.search_placeholder': 'Search by shipment number, consignee, or vessel...',
    'dashboard.status_label': 'Status:',
    'dashboard.sort_label': 'Sort:',
    'dashboard.sort_created': 'Created Date',
    'dashboard.sort_etd': 'Sailing Date (ETD)',
    'dashboard.sort_ref': 'Shipment #',
    'dashboard.sort_status': 'Status',
    'dashboard.per_page': 'Per page:',
    'dashboard.active_filters': 'Active filters:',
    'dashboard.clear_all': 'Clear all',
    'dashboard.refresh_success': 'Refreshed',
    'dashboard.refresh_desc': 'Dashboard data updated',
    'dashboard.logout_success': 'Logged out',
    'dashboard.logout_desc': 'You have been successfully logged out',
    
    // ShipmentTable / Pagination / Due Modal
    'table.loading': 'Loading shipments...',
    'table.failed': 'Failed to load shipments',
    'table.no_shipments_title': 'No Shipments Found',
    'table.no_shipments_desc': 'Try adjusting your filters or search terms.',
    'table.no_shipments_assigned': 'No shipments assigned to your broker at this time. Check back soon or contact support.',
    'table.col_shipment': 'Shipment',
    'table.col_shipper': 'Shipper',
    'table.col_order': 'Order',
    'table.col_booking': 'Booking',
    'table.col_vessel': 'Vessel',
    'table.col_draft': 'Draft Deadline',
    'table.col_due_status': 'Due Status',
    'table.col_actions': 'Actions',
    'table.showing': 'Showing {start} to {end} of {total} shipments',
    'table.due_modal_title': 'Enter DUE Number',
    'table.due_label': 'DUE Number',
    'table.due_placeholder': 'Enter DUE number',
    'table.due_shipment_details': 'Shipment Details',
    'table.due_shipper': 'Shipper',
    'table.due_required': 'DUE Number Required',
    'table.due_required_desc': 'Please enter a DUE number to continue.',
    'table.due_saved_title': 'DUE Number Saved',
    'table.due_saved_desc': 'Proceeding to Draft BL form...',
    'table.pdf_generating_title': 'Generating PDF',
    'table.pdf_generating_desc': 'Please wait while we generate your Bill of Lading...',
    'table.pdf_success_title': 'Success',
    'table.pdf_success_desc': 'Bill of Lading PDF generated successfully',
    'table.pdf_failed_title': 'Error',
    'table.pdf_failed_desc': 'Failed to generate PDF. Please try again.',
    'table.due_save_failed': 'Failed to save DUE number',
    
    // ShipmentDetailsModal
    'details.title': 'Shipment Details',
    'details.loading': 'Loading shipment details...',
    'details.no_data': 'No shipment data available',
    'details.status': 'Status',
    'details.booking_ref': 'Booking Reference',
    'details.parties': 'Parties',
    'details.shipper': 'Shipper',
    'details.consignee': 'Consignee',
    'details.transport_route': 'Transport & Route',
    'details.vessel_voyage': 'Vessel / Voyage',
    'details.pol': 'Port of Loading',
    'details.pod': 'Port of Discharge',
    'details.place_delivery': 'Place of Delivery',
    'details.dates': 'Important Dates',
    'details.etd': 'ETD (Expected Sailing)',
    'details.deadline_draft': 'Draft Deadline',
    'details.draft_submitted': 'Draft Submitted',
    'details.cargo_details': 'Cargo Details',
    'details.description': 'Description',
    'details.gross_weight': 'Gross Weight',
    'details.measurement': 'Measurement (CBM)',
    'details.containers': 'Containers ({count})',
    'details.col_container': 'Container #',
    'details.col_type': 'Type',
    'details.col_seal': 'Seal',
    'details.col_tare': 'Tare',
    'details.marks_numbers': 'Marks and Numbers',
    'details.close_btn': 'Close',
    'details.edit_draft': 'Edit Draft',
    
    // UserProfileDrawer
    'profile.title': 'Profile Settings',
    'profile.subtitle': 'Manage your account information',
    'profile.email': 'Email Address',
    'profile.email_helper': 'Email cannot be changed',
    'profile.display_name': 'Display Name',
    'profile.display_name_placeholder': 'Enter your display name',
    'profile.full_name': 'Full Name',
    'profile.full_name_placeholder': 'Enter your full name',
    'profile.phone': 'Phone Number',
    'profile.phone_placeholder': 'Enter your phone number',
    'profile.broker': 'Broker',
    'profile.broker_helper': 'Contact admin to change broker assignment',
    'profile.logout_btn': 'Log Out',
    'profile.cancel_btn': 'Cancel',
    'profile.save_btn': 'Save Changes',
    'profile.success_title': 'Profile Updated',
    'profile.success_desc': 'Your profile has been successfully updated.',
    'profile.failed_title': 'Update Failed',
    'profile.failed_desc': 'Failed to update profile. Please try again.',
    'profile.coming_soon': 'Coming Soon',
    'profile.coming_soon_desc': 'Avatar upload feature will be available soon.',
    'profile.avatar_helper': 'Click camera icon to change avatar',
    'profile.avatar_aria': 'Change avatar',
    'profile.language_label': 'Language / Idioma',
    'profile.lang_pt': '🇧🇷 Português (BR-pt)',
    'profile.lang_en': '🇺🇸 English (US-en)',
    
    // DraftBillOfLadingTab
    'draft.loading': 'Loading shipment...',
    'draft.no_shipment_title': 'Select a shipment to create Bill of Lading',
    'draft.no_shipment_desc': 'Please select a shipment from the dashboard to continue',
    'draft.back_dashboard': 'Back to Dashboard',
    'draft.badge_saved': 'Draft Saved',
    'draft.form_subtitle': 'DRAFT OF BILL OF LADING FCL / FCL',
    'draft.submit_btn': 'Submit DRAFT',
    'draft.pdf_btn': 'DRAFT pdf',
    'draft.shipper': 'Shipper:',
    'draft.shipper_placeholder': 'Shipper name',
    'draft.shipper_addr_placeholder': 'Shipper address',
    'draft.consignee': 'Consignee:',
    'draft.consignee_placeholder': 'Consignee name',
    'draft.consignee_addr_placeholder': 'Consignee address',
    'draft.notify_party': 'Notify Party:',
    'draft.notify_copy': 'Copy from Consignee',
    'draft.notify_placeholder': 'Notify party name',
    'draft.notify_addr_placeholder': 'Notify party address',
    'draft.delivery_apply': 'For delivery of the goods apply to:',
    'draft.delivery_placeholder': 'Delivery instructions',
    'draft.pre_carriage': 'Pre-carriage by:',
    'draft.pre_carriage_placeholder': 'Pre-carriage method',
    'draft.vessel_voyage': 'Vessel | Voyage:',
    'draft.vessel_placeholder': 'Vessel',
    'draft.voyage_placeholder': 'Voyage',
    'draft.port_discharge': 'Port of discharge:',
    'draft.port_discharge_placeholder': 'Port of discharge',
    'draft.place_receipt': 'Place of receipt by pre-carrier:',
    'draft.place_receipt_placeholder': 'Place of receipt',
    'draft.port_loading': 'Port of loading:',
    'draft.port_loading_placeholder': 'Port of loading',
    'draft.place_delivery': 'Place of delivery by on-carrier:',
    'draft.place_delivery_placeholder': 'Place of delivery',
    'draft.marks_numbers': 'Marks and numbers:',
    'draft.marks_numbers_placeholder': 'Shipment marks and numbers',
    'draft.ncm_hs': 'NCM / HS Code:',
    'draft.ncm_hs_placeholder': 'NCM / HS Code',
    'draft.gross_weight': 'Gross Weight (KGS):',
    'draft.gross_weight_placeholder': 'Total gross weight',
    'draft.measurement': 'Measurement (CBM):',
    'draft.measurement_placeholder': 'Total measurement',
    'draft.freight_charges': 'FREIGHT AND CHARGES:',
    'draft.freight_placeholder': 'Freight details',
    'draft.prepaid': 'PREPAID',
    'draft.collect': 'COLLECT',
    'draft.freight_payable': 'Freight payable at:',
    'draft.freight_payable_placeholder': 'Freight payable at',
    'draft.original_bl': 'Number of Original B(s)/L:',
    'draft.place_date_issue': 'Place and date of issue:',
    'draft.place_date_issue_placeholder': 'Place and date of issue',
    'draft.signature': 'Signature:',
    'draft.signature_placeholder': 'Signature',
    'draft.cargo_description': 'Cargo description / Particulars furnished by shipper:',
    'draft.cargo_desc_placeholder': 'Detailed description of goods',
    'draft.container_details': 'Container details:',
    'draft.container_num': 'Container Number',
    'draft.container_type': 'Container Type',
    'draft.container_tare': 'Tare Weight',
    'draft.container_gross': 'Gross Weight',
    'draft.container_net': 'Net Weight',
    'draft.container_cbm': 'CBM',
    'draft.container_qty': 'Qty',
    'draft.container_seal': 'Seal Number',
    'draft.container_vgm': 'VGM',
    'draft.add_container': 'Add Container',
    'draft.remove_btn': 'Remove',
    'draft.toast_autofill_title': 'Auto-filled',
    'draft.toast_autofill_desc': 'BOL form populated from shipment data',
    'draft.toast_submitted_title': 'Submitted',
    'draft.toast_submitted_desc': 'Draft submitted successfully. You can now generate PDF.',
    'draft.toast_saved_title': 'Saved',
    'draft.toast_saved_desc': 'Draft and container details saved successfully',
    'draft.toast_error_save': 'Failed to save draft',
    'draft.toast_pdf_title': 'PDF Generation',
    'draft.toast_pdf_desc': 'PDF generation feature available after submission'
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Try to load initial language from localStorage, default to BR-pt
  const [language, setLanguageState] = useState<LanguageType>(() => {
    const saved = localStorage.getItem('app_language');
    if (saved === 'BR-pt' || saved === 'US-en') {
      return saved;
    }
    return 'BR-pt';
  });

  const setLanguage = (lang: LanguageType) => {
    setLanguageState(lang);
    localStorage.setItem('app_language', lang);
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    const dictionary = translations[language] || translations['BR-pt'];
    let text = dictionary[key] || key;
    
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        text = text.split(`{${paramKey}}`).join(String(paramValue));
      });
    }
    
    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
